import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React, { useRef } from 'react';
import _isNumber from 'lodash/isNumber';
import { SNAP_DISTANCE_FACTOR } from './ScrollableContent.constants';
import { ScrollableContentContextProvider } from './ScrollableContent.context';
import { getSnapScrollDistance, hasReachedHorizontalEnd, hasReachedHorizontalStart, ifScrollIntervalTooFrequent } from './ScrollableContent.utils';
import Content from './components/Content';
import NextTrigger from './components/NextTrigger';
import PreviousTrigger from './components/PreviousTrigger';
import { ChildrenPropType } from '../../../../services/proptypes';
import { classNames } from '../../../../utils/classname';
import './ScrollableContent.less';
var defaultProps = {
  children: null,
  content: null,
  className: '',
  intervalFactor: 5,
  // the smaller the number, the faster the scroll
  distanceFactor: 3,
  // controls how much distance the scroll moves
  direction: 'horizontal'
};
export default function ScrollableContent(_ref) {
  var _classNames;
  var children = _ref.children,
    content = _ref.content,
    className = _ref.className,
    intervalFactor = _ref.intervalFactor,
    distanceFactor = _ref.distanceFactor,
    direction = _ref.direction;
  var scrollContainerRef = useRef(null);
  var intervalRef = useRef(null);
  var isScrolling = useRef(false);
  var isSnap = distanceFactor === SNAP_DISTANCE_FACTOR;
  function smoothScrollTo(value, scrollDirection) {
    if (ifScrollIntervalTooFrequent(intervalFactor)) {
      scrollContainerRef.current.scrollLeft = value;
      return;
    }
    isScrolling.current = true;
    var onScroll = function onScroll() {
      var currentScroll = scrollContainerRef.current.scrollLeft;
      var scrollFinished = scrollDirection > 0 ? currentScroll >= value : currentScroll <= value;
      if (scrollFinished || hasReachedHorizontalEnd(scrollContainerRef) || hasReachedHorizontalStart(scrollContainerRef)) {
        scrollContainerRef.current.removeEventListener('scroll', onScroll);
        isScrolling.current = false;
      }
    };
    scrollContainerRef.current.addEventListener('scroll', onScroll);
    scrollContainerRef.current.scrollTo({
      left: value,
      behavior: 'smooth'
    });
  }
  function startMoving(scrollDirection) {
    function moveForwardBackward() {
      if (isScrolling.current) {
        return;
      }
      if (scrollContainerRef.current) {
        var currentScroll = scrollContainerRef.current.scrollLeft;
        if (isSnap) {
          smoothScrollTo(currentScroll + scrollDirection * getSnapScrollDistance(scrollContainerRef), scrollDirection);
        }
        if (_isNumber(distanceFactor)) {
          smoothScrollTo(currentScroll + scrollDirection * distanceFactor, scrollDirection);
        }
      }
    }
    moveForwardBackward();
    intervalRef.current = setInterval(moveForwardBackward, Math.max(intervalFactor, 1));
  }
  function startMovingForward() {
    startMoving(1);
  }
  function stopMovingForward() {
    clearInterval(intervalRef.current);
    isScrolling.current = false;
  }
  function startMovingBackward() {
    startMoving(-1);
  }
  function stopMovingBackward() {
    clearInterval(intervalRef.current);
    isScrolling.current = false;
  }
  var contextValue = {
    scrollContainerRef: scrollContainerRef,
    startMovingForward: startMovingForward,
    stopMovingForward: stopMovingForward,
    startMovingBackward: startMovingBackward,
    stopMovingBackward: stopMovingBackward
  };
  var finalContent = content || children;
  var finalClassName = classNames((_classNames = {
    'scrollable-content': true
  }, _defineProperty(_classNames, className, className), _defineProperty(_classNames, direction, true), _defineProperty(_classNames, "snap", isSnap), _classNames));
  return /*#__PURE__*/React.createElement(ScrollableContentContextProvider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement("div", {
    className: finalClassName
  }, finalContent));
}
ScrollableContent.defaultProps = defaultProps;
ScrollableContent.Content = Content;
ScrollableContent.PreviousTrigger = PreviousTrigger;
ScrollableContent.NextTrigger = NextTrigger;